/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import {
  Alert,
  Colors,
  Field,
  Icon,
  LegacyButton,
  Link,
  ServiceTypeahead,
  Spacer,
  Text,
  TooltipWrapper,
  Typeahead,
} from '@zapier/design-system';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { flatMap } from 'lodash';
import { Fragment, useRef, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { sleep } from '../insights/utils';
import {
  ZapierActionNeeds,
  NeedsItemEditor,
  createPlaceholder,
} from './NeedsItemEditor';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';

const styleExampleURL = css`
  /* Override AppBuilder styles */
  margin: 20px 0 !important;
  padding: 25px;
  background: ${Colors.PrimeBlack};
  color: ${Colors.PrimeWhite};
  white-space: pre-wrap;
  /* Line wrap on any character since words will get very long */
  word-break: break-all;
  border-radius: 5px;

  span {
    color: ${Colors.StatusWarningStronger};
  }
`;

const styleBox = css`
  border: 1px solid ${Colors.GrayWarm5};
  padding: 20px;
  margin-top: 20px;
  border-radius: var(--zds-radius-small);
`;

const styleInnerBox = css`
  ${styleBox};
  background: ${Colors.GrayWarm2};
`;

const styleBoxDivider = css`
  border-top: 1px solid ${Colors.GrayWarm5};
  margin: 30px -30px;
`;

const styleFlexGap = css`
  display: flex;
  gap: 10px;
`;

async function fetchTriggersOrActions(
  selectedAPI: string
): Promise<ZapierAction[]> {
  const url = new URL('/api/v4/implementations/', ZAPIER_APP_BASE_URL);
  url.searchParams.set('global', 'true');
  url.searchParams.set('limit', '1');
  url.searchParams.set('selected_apis', selectedAPI);
  const resp = await fetch(url.href);
  if (!resp.ok) {
    throw new Error(resp.statusText);
  }
  const json: ZapierTriggerOrActionResponse = await resp.json();
  return flatMap(json.results, d => d.actions).filter(d => !d.is_hidden);
}

async function fetchTriggers(selectedAPI: string): Promise<ZapierAction[]> {
  const data = await fetchTriggersOrActions(selectedAPI);
  return data.filter(d => d.type === 'read');
}

async function fetchActions(selectedAPI: string): Promise<ZapierAction[]> {
  const data = await fetchTriggersOrActions(selectedAPI);
  return data.filter(d => d.type === 'write');
}

async function fetchNeeds({
  selectedAPI,
  actionKey,
  actionType,
}: {
  selectedAPI: string;
  actionKey: string;
  actionType: 'read' | 'write';
}): Promise<ZapierActionNeeds[]> {
  const url = new URL(
    '/api/v4/implementations/needs/public/',
    ZAPIER_APP_BASE_URL
  );
  const resp = await fetch(url.href, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      selected_api: selectedAPI,
      action: actionKey,
      type_of: actionType,
    }),
  });
  if (!resp.ok) {
    throw new Error(resp.statusText);
  }
  const json: ZapierActionNeedsResponse = await resp.json();
  if (!json.success) {
    throw new Error(
      `Failed to fetch needs: selectedAPI=${selectedAPI} actionKey=${actionKey} actionType=${actionType}`
    );
  }
  return json.needs.filter(d => !d.computed);
}

interface ZapierActionNeedsResponse {
  success: boolean;
  needs: ZapierActionNeeds[];
}

interface ZapierAction {
  type: string;
  key: string;
  name: string;
  noun: string;
  description: string;
  description_html: string;
  is_important: boolean;
  is_hidden: boolean;
  needs: ZapierActionNeeds[];
  meta: {
    bulk_action: string;
  };
}

interface ZapierTriggerOrActionResponse {
  count: number;
  next: string;
  previous: string;
  results: {
    selected_api: string;
    app_id: number;
    auth_type: string;
    auth_fields: {
      required: boolean;
      type: string;
      key: string;
    }[];
    actions: ZapierAction[];
    is_deprecated: boolean;
    is_private_only: boolean;
    is_invite_only: boolean;
    is_beta: boolean;
    is_premium: boolean;
    is_hidden: boolean;
    name: string;
    slug: string;
    images: {
      url_16x16: string;
      url_32x32: string;
      url_64x64: string;
      url_128x128: string;
    };
    primary_color: string;
    secondary_color: string;
  }[];
}

interface ZapierService {
  id: string;
  canonicalId: string;
  slug: string;
  name: string;
  currentImplementation: {
    id: string;
  };
  images: {
    url_16x16: string;
    url_32x32: string;
    url_64x64: string;
    url_128x128: string;
  };
}

// Like regular encodeURIComponent but leave {curly braces} alone so the URL is
// easier to read
function encodeURIComponentPrettier(str: string): string {
  return encodeURIComponent(str)
    .replace(/%7B/g, '{')
    .replace(/%7D/g, '}')
    .replace(/%40/g, '@');
}

export function appImplementationIdToSelectedAPI(id: string) {
  if (id) {
    const parts = id.split('@');
    if (parts.length > 1) {
      parts[1] = 'latest'; // CLI apps should be set to "latest" version so links don't get out of date
    }

    return parts.join('@');
  }

  return id;
}

type ZapPrefillSelectProps = {
  serviceSlug: string;
};

export function ZapPrefillSelect({
  serviceSlug,
}: ZapPrefillSelectProps): JSX.Element {
  const [copyMessage, setCopyMessage] = useState('');

  const preRef = useRef<HTMLPreElement>(null);

  // Trigger data
  const [triggerApp, setTriggerApp] = useState<ZapierService | undefined>();
  const triggerQuery = useQuery({
    queryKey: [triggerApp?.currentImplementation?.id, 'read'],
    async queryFn({ queryKey: [selectedAPI] }) {
      return await fetchTriggers(selectedAPI);
    },
    enabled: Boolean(triggerApp),
  });
  const [selectedTrigger, setSelectedTrigger] = useState<
    ZapierAction | undefined
  >();
  const triggerNeedsQuery = useQuery({
    queryKey: [
      triggerApp?.currentImplementation?.id,
      selectedTrigger?.key,
      'read',
    ] as const,
    async queryFn({ queryKey: [selectedAPI, actionKey, actionType] }) {
      return await fetchNeeds({ selectedAPI, actionKey, actionType });
    },
    enabled: Boolean(triggerApp && selectedTrigger),
  });
  const triggerItems =
    triggerQuery.status === 'success'
      ? triggerQuery.data.map(action => {
          return {
            action,
            name: action.name || `[${action.key}]`,
            value: action.key,
          };
        })
      : [];

  // Action data
  const [actionApp, setActionApp] = useState<ZapierService | undefined>();
  const actionQuery = useQuery({
    queryKey: [actionApp?.currentImplementation?.id, 'write'],
    async queryFn({ queryKey: [selectedAPI] }) {
      return await fetchActions(selectedAPI);
    },
    enabled: Boolean(actionApp),
  });
  const [selectedAction, setSelectedAction] = useState<
    ZapierAction | undefined
  >();
  const actionNeedsQuery = useQuery({
    queryKey: [
      actionApp?.currentImplementation?.id,
      selectedAction?.key,
      'write',
    ] as const,
    async queryFn({ queryKey: [selectedAPI, actionKey, actionType] }) {
      return await fetchNeeds({ selectedAPI, actionKey, actionType });
    },
    enabled: Boolean(actionApp && selectedAction),
  });
  const actionItems =
    actionQuery.status === 'success'
      ? actionQuery.data.map(action => {
          return {
            action,
            name: action.name || `[${action.key}]`,
            value: action.key,
          };
        })
      : [];

  const [triggerQueryData, setTriggerQueryData] = useState(
    new Map<string, string | undefined>()
  );
  const [actionQueryData, setActionQueryData] = useState(
    new Map<string, string | undefined>()
  );

  const clearTriggerActionRef = useRef(() => {});
  const clearActionActionRef = useRef(() => {});

  useEffect(() => {
    setTriggerQueryData(new Map());
    if (clearTriggerActionRef.current) {
      clearTriggerActionRef.current();
    }
  }, [triggerApp]);

  useEffect(() => {
    setActionQueryData(new Map());
    if (clearActionActionRef.current) {
      clearActionActionRef.current();
    }
  }, [actionApp]);

  useEffect(() => {
    setTriggerQueryData(new Map());
  }, [selectedTrigger]);

  useEffect(() => {
    setActionQueryData(new Map());
  }, [selectedAction]);

  function createParams() {
    const p = new URLSearchParams();
    if (triggerApp?.currentImplementation?.id) {
      p.set(
        'steps[0][app]',
        appImplementationIdToSelectedAPI(triggerApp.currentImplementation?.id)
      );
    }
    if (selectedTrigger) {
      p.set('steps[0][action]', selectedTrigger.key);
    }
    if (triggerNeedsQuery.status === 'success') {
      const triggerNeedsPlaceholders = new Map<string, string>();
      for (const needs of triggerNeedsQuery.data) {
        triggerNeedsPlaceholders.set(
          needs.key,
          createPlaceholder(['trigger', needs.key].join(' '))
        );
      }
      for (const [key, val] of triggerQueryData.entries()) {
        p.set(
          `steps[0][params][${key}]`,
          val || triggerNeedsPlaceholders.get(key) || ''
        );
      }
    }
    if (actionApp?.currentImplementation?.id) {
      p.set(
        'steps[1][app]',
        appImplementationIdToSelectedAPI(actionApp.currentImplementation?.id)
      );
    }
    if (selectedAction) {
      p.set('steps[1][action]', selectedAction.key);
    }
    if (actionNeedsQuery.status === 'success') {
      const actionNeedsPlaceholders = new Map<string, string>();
      for (const needs of actionNeedsQuery.data) {
        actionNeedsPlaceholders.set(
          needs.key,
          createPlaceholder(['action', needs.key].join(' '))
        );
      }
      for (const [key, val] of actionQueryData.entries()) {
        p.set(
          `steps[1][params][${key}]`,
          val || actionNeedsPlaceholders.get(key) || ''
        );
      }
    }
    return p.toString();
  }

  const params = createParams();

  const zapURLWithParams = new URL(
    `/v1/embed/${serviceSlug}/create`,
    'https://api.zapier.com'
  );
  const zapBaseURL = new URL(zapURLWithParams.href);
  zapURLWithParams.search = params;

  const isNotReadyForURL = Boolean(
    !(triggerApp && selectedTrigger) && actionApp
  );

  const triggerAppWithNoTriggersSelected =
    (triggerApp && !triggerApp.currentImplementation?.id) ||
    (triggerQuery.status === 'success' && triggerItems.length === 0);
  const actionAppWithNoActionsSelected =
    (actionApp && !actionApp.currentImplementation?.id) ||
    (actionQuery.status === 'success' && actionItems.length === 0);

  return (
    <div>
      <div css={styleBox}>
        <Text fontWeight={600} tag="h3" type="ParagraphHeader1">
          Step 1: Pre-fill fields for your users
        </Text>
        <Text margin="20px 0 0 0" tag="p" type="Body1">
          Search for an app and event for the trigger and action to see the
          fields you can pre-fill.{' '}
          <Link
            href={
              new URL(
                '/embed/zap-editor#using-the-pre-filled-zap-generator',
                BASE_DOC_URL_VB
              ).href
            }
            target="_blank"
          >
            Learn how to use the pre-filled Zap generator below.
          </Link>
        </Text>

        <div css={styleInnerBox}>
          <Text tag="h4" type="ParagraphHeader3">
            Trigger
          </Text>
          <Text margin="15px 0 0 0" tag="p" type="Body1">
            A trigger is an event that starts your Zap.
          </Text>
          <Spacer height={15} />
          <ServiceTypeahead
            apiHostname={process.env.ZAPIER_API_BASE_URL}
            menuAriaLabel="Search for an app"
            onChange={setTriggerApp}
            placeholder="Search for an app"
          />
          {triggerAppWithNoTriggersSelected && (
            <Text color="StatusErrorStronger" margin="5px 0 0 0" tag="div">
              This app does not have a trigger. Please select a different app.
            </Text>
          )}
          <Spacer height={30} />
          <Field
            error={
              !triggerApp && isNotReadyForURL
                ? 'A trigger must be selected.'
                : undefined
            }
            label="Event"
            renderHelpText={() => <Text>This is what starts the Zap.</Text>}
            renderInput={fieldProps => (
              <Typeahead
                {...fieldProps}
                getLabelForItem={item => item.name}
                isBusy={triggerQuery.status === 'loading'}
                isDisabled={!triggerApp || triggerAppWithNoTriggersSelected}
                isErrored={triggerQuery.status === 'error'}
                items={triggerItems}
                menuAriaLabel="Search for an event"
                onChange={item => {
                  setSelectedTrigger(item?.action);
                }}
                openOnFocus={true}
                placeholder="Search for an event"
                setClearSelection={clear =>
                  (clearTriggerActionRef.current = clear as any)
                }
              />
            )}
          />
          {triggerNeedsQuery.status === 'success' &&
            triggerNeedsQuery.data.length > 0 && (
              <Fragment>
                <div css={styleBoxDivider} />
                <Text tag="h4" type="ParagraphHeader1">
                  Select the {selectedTrigger.name} fields you want to pre-fill:{' '}
                  <Icon name="arrowBigDown" size={16} />
                </Text>
                <Text margin="15px 0" tag="p" type="Body1">
                  If the value is the same for every user, you can fill them in
                  now. If they are dynamic or you don&rsquo;t know them yet,
                  copy the URL in the testing step below and replace the
                  placeholder values before using it in your app.
                </Text>
                {triggerNeedsQuery.data.map(need => {
                  return (
                    <NeedsItemEditor
                      isEnabled={triggerQueryData.has(need.key)}
                      need={need}
                      onChange={value => {
                        const map = new Map(triggerQueryData);
                        if (value === undefined) {
                          map.delete(need.key);
                        } else {
                          map.set(need.key, value);
                        }
                        setTriggerQueryData(map);
                      }}
                      step={0}
                      value={triggerQueryData.get(need.key)}
                    />
                  );
                })}
              </Fragment>
            )}
        </div>

        <Spacer height={10} />

        <div css={styleInnerBox}>
          <Text tag="h4" type="ParagraphHeader3">
            Action
          </Text>
          <Text margin="15px 0 0 0" tag="p" type="Body1">
            An action is an event a Zap performs after it starts.
          </Text>
          <Spacer height={15} />
          <ServiceTypeahead
            apiHostname={process.env.ZAPIER_API_BASE_URL}
            menuAriaLabel="Search for an app"
            onChange={setActionApp}
            placeholder="Search for an app"
          />
          {actionAppWithNoActionsSelected && (
            <Text color="StatusErrorStronger" margin="5px 0 0 0" tag="div">
              This app does not have an action. Please select a different app.
            </Text>
          )}
          <Spacer height={30} />
          <Field
            label="Event"
            renderHelpText={() => (
              <Text>This is performed when the Zap runs.</Text>
            )}
            renderInput={fieldProps => (
              <Typeahead
                {...fieldProps}
                getLabelForItem={item => item.name}
                isBusy={actionQuery.status === 'loading'}
                isDisabled={!actionApp || actionAppWithNoActionsSelected}
                isErrored={actionQuery.status === 'error'}
                items={actionItems}
                menuAriaLabel="Search for an event"
                onChange={item => {
                  setSelectedAction(item?.action);
                }}
                openOnFocus={true}
                placeholder="Search for an event"
                setClearSelection={clear =>
                  (clearActionActionRef.current = clear as any)
                }
              />
            )}
          />
          {actionNeedsQuery.status === 'success' &&
            actionNeedsQuery.data.length > 0 && (
              <Fragment>
                <div css={styleBoxDivider} />
                <Text tag="h4" type="ParagraphHeader1">
                  Select the {selectedAction.name} fields you want to pre-fill:
                  <Icon name="arrowBigDown" size={16} />
                </Text>
                <Text tag="p" type="Body1">
                  If the value is the same for every user, you can fill them in
                  now. If they are dynamic or you don&rsquo;t know them yet,
                  copy the URL in the testing step below and replace the
                  placeholder values before using it in your app.
                </Text>
                {actionNeedsQuery.data.map(need => {
                  return (
                    <NeedsItemEditor
                      isEnabled={actionQueryData.has(need.key)}
                      need={need}
                      onChange={value => {
                        const map = new Map(actionQueryData);
                        if (value === undefined) {
                          map.delete(need.key);
                        } else {
                          map.set(need.key, value);
                        }
                        setActionQueryData(map);
                      }}
                      step={1}
                      value={actionQueryData.get(need.key)}
                    />
                  );
                })}
              </Fragment>
            )}
        </div>

        <Spacer height={50} />

        <div>
          <Text fontWeight={600} tag="h3" type="ParagraphHeader1">
            Step 2: Test pre-filled Zap &amp; copy URL
          </Text>
          <Text margin="20px 0 0 0" tag="p" type="Body1">
            Below is the create Zap URL that contains the pre-filled Zap you
            have defined. You can preview it to see how it will look for your
            users. Copy the URL and embed it inside your app so your users can
            set up a Zap easier and faster.
          </Text>
          {isNotReadyForURL ? (
            // Show an error if the user selects an action without a trigger
            <div css={{ margin: '20px 0' }}>
              <Alert status="warning">
                A trigger app must be selected to generate the pre-filled Zap
                URL. Please select a trigger in step 1.
              </Alert>
            </div>
          ) : (
            <pre css={styleExampleURL} ref={preRef}>
              {zapBaseURL.href}?
              {Array.from(
                new URLSearchParams(params)
                  // @ts-ignore Outdated TS doesn't think .entries() exists
                  .entries()
              ).map(([key, val], index) => {
                const prefix = index > 0 ? '&' : '';
                if (
                  [
                    'steps[0][app]',
                    'steps[0][action]',
                    'steps[1][app]',
                    'steps[1][action]',
                  ].includes(key)
                ) {
                  return (
                    <Fragment key={key}>
                      {prefix}
                      {key}={val}
                    </Fragment>
                  );
                } else {
                  return (
                    <Fragment key={key}>
                      {prefix}
                      {key}=<span>{encodeURIComponentPrettier(val)}</span>
                    </Fragment>
                  );
                }
              })}
            </pre>
          )}
          <div css={styleFlexGap}>
            <LegacyButton
              // Workaround for Zinnia v5 UI bug where disabled + secondary
              // doesn't look correct
              // https://zapier.slack.com/archives/C5Z9BP4U9/p1685050412123769
              color={!params || isNotReadyForURL ? 'primary' : 'secondary'}
              disabled={!params || isNotReadyForURL}
              href={zapURLWithParams.href}
              target="_blank"
            >
              Test Zap
            </LegacyButton>
            <TooltipWrapper content={copyMessage} position="east">
              {() => (
                <LegacyButton
                  disabled={!params || isNotReadyForURL}
                  iconBefore="actionCopy"
                  onClick={async () => {
                    if (!preRef.current) {
                      return;
                    }
                    try {
                      navigator.clipboard.writeText(preRef.current.textContent);
                      setCopyMessage('Copied!');
                    } catch (err) {
                      setCopyMessage('Failed to copy');
                    } finally {
                      await sleep(2000);
                      setCopyMessage('');
                    }
                  }}
                >
                  Copy Zap URL
                </LegacyButton>
              )}
            </TooltipWrapper>
          </div>
        </div>
      </div>
    </div>
  );
}
